import React, { useEffect, useState } from "react";
import { Button, CloseButton, Modal, Row, Col, Alert } from "react-bootstrap";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import { CIRCLEONE_USER } from "../../constants";

interface IProps {
  closeFilterModal: () => void;
  selectedFilterAndValue: any;
  setSelectedFilterAndValue: any;
  handleSubmit: (selectedFilterValues: any) => void;
  setIsLoadingPageChange: any;
}

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const TicketFilterModal: React.FC<IProps> = ({
  closeFilterModal,
  selectedFilterAndValue,
  setSelectedFilterAndValue,
  handleSubmit,
  setIsLoadingPageChange,
}) => {
  const [selectedTicketFilters, setSelectedTicketFilters] = useState<string[]>(
    []
  );
  const [dateTo, setDateTo] = useState<string>("");
  const [suggestions, setSuggestions] = useState([]);
  const [assigneeSuggestions, setAssigneeSuggestions] = useState([]);
  const [error, setError] = useState<string | null>(null);
  const [emailValidationError, setEmailValidationError] = useState<
    string | null
  >(null);

  const tenantName = localStorage.getItem(CIRCLEONE_USER);

  const filters = [
    { label: "Customer email", value: "action__contact__email__icontains" },
    { label: "Assignee", value: "action__assignee__username__icontains" },
    { label: "Title", value: "title__icontains" },
    { label: "Date", value: "created" },
    { label: "Ticket id", value: "id__icontains" },
    ...(tenantName === "victorinox"
      ? [{ label: "Token Id", value: "token_id" }]
      : [{ label: "Merchant id", value: "merchant_id" }]),
    ...(tenantName === "cslfinance" || tenantName === "meta" || tenantName === "stage" ? [{ label: "Loan ID", value: "loan_id" }] : []),
  ];

  useEffect(() => {
    const savedFilters = sessionStorage.getItem("selectedTicketFilters");
    const savedFilterValues = sessionStorage.getItem("selectedFilterAndValue");
    if (savedFilters && savedFilterValues) {
      setSelectedTicketFilters(JSON.parse(savedFilters));
      setSelectedFilterAndValue(JSON.parse(savedFilterValues));
      setDateTo(JSON.parse(savedFilterValues).created || "");
    } else {
      const appliedFilters = Object.keys(selectedFilterAndValue).filter(
        (key) => selectedFilterAndValue[key]
      );
      setSelectedTicketFilters(appliedFilters);
      setDateTo(selectedFilterAndValue.created || "");
    }
  }, [setSelectedFilterAndValue]);

  useEffect(() => {
    sessionStorage.setItem(
      "selectedTicketFilters",
      JSON.stringify(selectedTicketFilters)
    );
    sessionStorage.setItem(
      "selectedFilterAndValue",
      JSON.stringify(selectedFilterAndValue)
    );
  }, [selectedTicketFilters, selectedFilterAndValue]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedTicketFilters((prevSelectedFilters) => {
      let updatedFilters;
      if (prevSelectedFilters.includes(value)) {
        updatedFilters = prevSelectedFilters.filter(
          (filter) => filter !== value
        );
        setSelectedFilterAndValue((prev) => ({
          ...prev,
          [value]: "",
        }));
        if (value === "action__contact__email__icontains") {
          setEmailValidationError(null);
        }
      } else {
        updatedFilters = [...prevSelectedFilters, value];
      }
      handleSubmit(
        updatedFilters.reduce((acc, filter) => {
          acc[filter] = selectedFilterAndValue[filter] || "";
          return acc;
        }, {})
      );
      return updatedFilters;
    });
  };

  const handleFilterValueChange = (value: string, filter: string) => {
    setSelectedFilterAndValue((prevFilterValues) => ({
      ...prevFilterValues,
      [filter]: value,
    }));
  };

  const handleDateToChange = (event: any) => {
    const value = event.target.value;
    setDateTo(value);
    setSelectedFilterAndValue((prev) => ({
      ...prev,
      created: value,
    }));
  };

  const resetFilters = () => {
    setSelectedTicketFilters([]);
    const emptyFilters = filters.reduce((acc, filter) => {
      acc[filter.value] = "";
      return acc;
    }, {});
    setSelectedFilterAndValue(emptyFilters);
    setDateTo("");
    setError(null);
    setEmailValidationError(null);
    sessionStorage.removeItem("selectedTicketFilters");
    sessionStorage.removeItem("selectedFilterAndValue");
    handleSubmit({});
  };

  const cleanEmptyFiltersAndClose = () => {
    const filteredTicketFilters = selectedTicketFilters.filter(
      (filter) =>
        selectedFilterAndValue[filter] &&
        selectedFilterAndValue[filter].toString().trim() !== ""
    );

    const filteredFilterValues = filteredTicketFilters.reduce((acc, filter) => {
      acc[filter] = selectedFilterAndValue[filter];
      return acc;
    }, {});

    setSelectedTicketFilters(filteredTicketFilters);
    setSelectedFilterAndValue(filteredFilterValues);

    sessionStorage.setItem(
      "selectedTicketFilters",
      JSON.stringify(filteredTicketFilters)
    );
    sessionStorage.setItem(
      "selectedFilterAndValue",
      JSON.stringify(filteredFilterValues)
    );

    closeFilterModal();
  };

  const apiPort = process.env.REACT_APP_API_PROTOCOL;
  const fetchingTenant = window.location.hostname.split(".")[0];

  const searchCustomerEmail = (skey: string) => {
    axios
      .get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/customers/contacts/search-by-email/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { email: skey },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setSuggestions(res.data);
          setError(null);
        }
      })
      .catch(() => {
        setError("Failed to fetch customer email suggestions.");
      });
  };

  const searchAssigneeSuggestions = (skey: string) => {
    axios
      .get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/users/users/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { username__icontains: skey },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setAssigneeSuggestions(res.data);
          setError(null);
        }
      })
      .catch(() => {
        setError("Failed to fetch assignee suggestions.");
      });
  };

  const handleApplyFilters = () => {
    let filtersWithValues = selectedTicketFilters.filter((filter) => {
      const value = selectedFilterAndValue[filter];
      if (filter === "action__contact__email__icontains") {
        if (!value || value.trim() === "") return false;
        if (!isValidEmail(value)) {
          setEmailValidationError("Please enter a valid email address.");
          return false;
        }
      }
      return value && value.toString().trim() !== "";
    });

    if (
      selectedTicketFilters.includes("action__contact__email__icontains") &&
      (!selectedFilterAndValue["action__contact__email__icontains"] ||
        !isValidEmail(
          selectedFilterAndValue["action__contact__email__icontains"]
        ))
    ) {
      setEmailValidationError("Please enter a valid email address.");
      return;
    } else {
      setEmailValidationError(null);
    }

    const filterValuesToSubmit = filtersWithValues.reduce((acc, filter) => {
      acc[filter] = selectedFilterAndValue[filter];
      return acc;
    }, {});

    setSelectedTicketFilters(filtersWithValues);
    setSelectedFilterAndValue(filterValuesToSubmit);
    sessionStorage.setItem(
      "selectedTicketFilters",
      JSON.stringify(filtersWithValues)
    );
    sessionStorage.setItem(
      "selectedFilterAndValue",
      JSON.stringify(filterValuesToSubmit)
    );

    handleSubmit(filterValuesToSubmit);
    setIsLoadingPageChange(true);
    closeFilterModal();
  };

  return (
    <Modal
      show={true}
      onHide={cleanEmptyFiltersAndClose}
      centered
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title className="fw-bold">Filters</Modal.Title>
        <CloseButton onClick={cleanEmptyFiltersAndClose} />
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="px-3 py-2">
          {filters.map((filter) => (
            <Row key={filter.value} className="mb-3 align-items-center">
              <Col xs={12} md={4} className="text-nowrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTicketFilters.includes(filter.value)}
                      onChange={handleFilterChange}
                      value={filter.value}
                      color="primary"
                    />
                  }
                  label={filter.label}
                />
              </Col>
              <Col xs={12} md={8}>
                {selectedTicketFilters.includes(filter.value) && (
                  <>
                    {filter.value === "created" ? (
                      <TextField
                        id="date-to"
                        label="Select Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: { style: { textTransform: "uppercase" } },
                        }}
                        fullWidth
                        value={dateTo}
                        onChange={handleDateToChange}
                      />
                    ) : filter.value === "action__contact__email__icontains" ? (
                      <Autocomplete
                        freeSolo
                        options={suggestions.map((option) => option.email)}
                        value={selectedFilterAndValue[filter.value] || ""}
                        onInputChange={(event, newInputValue) => {
                          searchCustomerEmail(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                          const selectedSuggestion = suggestions.find(
                            (suggestion) => suggestion.email === newValue
                          );
                          setSelectedFilterAndValue((prev) => ({
                            ...prev,
                            action__contact__email__icontains:
                              selectedSuggestion
                                ? selectedSuggestion.email
                                : newValue || "",
                          }));
                          if (
                            newValue &&
                            isValidEmail(newValue) &&
                            emailValidationError
                          ) {
                            setEmailValidationError(null);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter.label}
                            variant="outlined"
                            error={!!emailValidationError}
                            helperText={emailValidationError || ""}
                          />
                        )}
                      />
                    ) : filter.value ===
                      "action__assignee__username__icontains" ? (
                      <Autocomplete
                        freeSolo
                        options={assigneeSuggestions.map(
                          (option) => option.username
                        )}
                        value={selectedFilterAndValue[filter.value] || ""}
                        onInputChange={(event, newInputValue) => {
                          searchAssigneeSuggestions(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                          const selectedSuggestion = assigneeSuggestions.find(
                            (suggestion) => suggestion.username === newValue
                          );
                          setSelectedFilterAndValue((prev) => ({
                            ...prev,
                            action__assignee__username__icontains:
                              selectedSuggestion
                                ? selectedSuggestion.username
                                : "",
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter.label}
                            variant="outlined"
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        label={filter.label}
                        fullWidth
                        value={selectedFilterAndValue[filter.value] || ""}
                        onChange={(e) =>
                          handleFilterValueChange(e.target.value, filter.value)
                        }
                      />
                    )}
                  </>
                )}
              </Col>
            </Row>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={resetFilters}>
          Reset
        </Button>
        <Button variant="primary" onClick={handleApplyFilters}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TicketFilterModal;
